import { useState, type JSX } from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import type { JizdyFilters as OrdersFiltersShape } from '@/api/types/Jizda';
import { BrandChip } from '@/components/shell';
import {
  RegistryAutocomplete,
  type RegistryValue,
} from '@/components/registry/RegistryAutocomplete';
import { formatDateCs, todayIsoDate } from '@/utils/date';

type AssignmentValue = 'all' | 'assigned' | 'unassigned';

const assignmentToFilter = (v: AssignmentValue): boolean | undefined => {
  if (v === 'all') return undefined;
  return v === 'assigned';
};

const filterToAssignment = (assigned: boolean | undefined): AssignmentValue => {
  if (assigned === undefined) return 'all';
  return assigned ? 'assigned' : 'unassigned';
};

// Tri-state storno filter (CHR-02 / ADR-0039 / zadání §10), mirroring the
// assignment tri-state: `all` → undefined (default; STORNO rows show red
// inline), `only` → true (only storno), `hide` → false (exclude storno).
type StornoValue = 'all' | 'only' | 'hide';

const stornoToFilter = (v: StornoValue): boolean | undefined => {
  if (v === 'all') return undefined;
  return v === 'only';
};

const filterToStorno = (storno: boolean | undefined): StornoValue => {
  if (storno === undefined) return 'all';
  return storno ? 'only' : 'hide';
};

export interface OrdersFiltersProps {
  filters: OrdersFiltersShape;
  onChange: (next: Partial<OrdersFiltersShape>) => void;
}

// Vapas compact top toolbar (DESIGN.md §7/§8.1 + mockup `.toolbar`): inline
// label-mini + field-style controls, flex spacer, and the active `.filter`
// chip on the right (the date filter is always present, defaults to today).
// CHR-02: the accordion is gone (flat jízdy) so the expand-all/collapse-all
// controls are removed.
export const OrdersFilters = ({
  filters,
  onChange,
}: OrdersFiltersProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');

  const assignmentValue = filterToAssignment(filters.assigned);
  const stornoValue = filterToStorno(filters.storno);

  // Commodity filter (CHR-04 / ADR-0043). Only the resolved `commodityId` (a
  // registry pick) feeds the server query; a free-typed, unpersisted value has
  // no id so it cannot filter — clearing it removes the restriction. The
  // selected commodity's display name is kept locally for the input (the
  // filter shape carries only the id). Debounced server fetch in the
  // autocomplete means newly-added commodities appear + many values scroll by
  // construction.
  const [commodityValue, setCommodityValue] = useState<RegistryValue | null>(null);
  const handleCommodityChange = (next: RegistryValue | null): void => {
    setCommodityValue(next && next.id ? next : null);
    onChange({ commodityId: next?.id });
  };

  // The date filter is always present and defaults to today (api default +
  // slice initialState agree). The active-filter chip is therefore only shown
  // when a NON-default (non-today) lower bound is active; clearing it resets
  // `from` to today (the default), which removes the chip and gives the
  // clear-X a real effect — never an empty string (an empty `from` would 400
  // against the api's `LocalDate` binding).
  const today = todayIsoDate();
  const isNonDefaultFrom = Boolean(filters.from) && filters.from !== today;
  const resetFrom = (): void => onChange({ from: today });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        // DESIGN.md `.toolbar` padding: 12px top / 16px bottom; transparent —
        // it floats between the view toolbar and the table card.
        pt: theme.spacing(1.5),
        pb: theme.spacing(2),
        flexWrap: 'wrap',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 10.5,
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        {t('filter.from')}
      </Typography>
      <TextField
        type="date"
        autoComplete="off"
        // Clearable — an empty "od" is a legal UNBOUNDED filter, so the empty
        // state is reachable and needs the §14 WebKit mask hook.
        inputProps={{ 'data-empty': filters.from === '' ? 'true' : undefined }}
        value={filters.from}
        onChange={(e) => onChange({ from: e.target.value })}
        size="small"
        aria-label={t('filter.from')}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        select
        size="small"
        label={t('filter.assignment')}
        value={assignmentValue}
        onChange={(e) =>
          onChange({ assigned: assignmentToFilter(e.target.value as AssignmentValue) })
        }
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="all">{t('filter.assignmentAll')}</MenuItem>
        <MenuItem value="assigned">{t('filter.assignmentAssigned')}</MenuItem>
        <MenuItem value="unassigned">{t('filter.assignmentUnassigned')}</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        label={t('filter.storno')}
        value={stornoValue}
        onChange={(e) =>
          onChange({ storno: stornoToFilter(e.target.value as StornoValue) })
        }
        sx={{ minWidth: 160 }}
        data-testid="filter-storno"
      >
        <MenuItem value="all">{t('filter.stornoAll')}</MenuItem>
        <MenuItem value="only">{t('filter.stornoOnly')}</MenuItem>
        <MenuItem value="hide">{t('filter.stornoHide')}</MenuItem>
      </TextField>
      {/* Commodity filter (CHR-04) — same číselník autocomplete as the editor;
          feeds `commodityId` into the list query. */}
      <Box sx={{ minWidth: 180 }} data-testid="filter-commodity">
        <RegistryAutocomplete
          kind="commodity"
          label={t('filter.commodity')}
          value={commodityValue}
          onChange={handleCommodityChange}
        />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.showDeleted}
            onChange={(e) => onChange({ showDeleted: e.target.checked })}
          />
        }
        label={t('filter.showDeleted')}
        sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
      />
      <Box sx={{ flex: 1 }} />
      {isNonDefaultFrom ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label={t('filter.clearFrom')}
          onClick={resetFrom}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') resetFrom();
          }}
          sx={{ cursor: 'pointer', display: 'inline-flex' }}
          data-testid="active-filter-from"
        >
          <BrandChip variant="brand">
            <Stack direction="row" alignItems="center" gap={0.5} component="span">
              {t('filter.fromChip', { date: formatDateCs(filters.from) })}
              <CloseIcon sx={{ fontSize: 13 }} />
            </Stack>
          </BrandChip>
        </Box>
      ) : null}
    </Box>
  );
};
